if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.header = document.querySelector('.header-wrapper');
        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button.localization-form__select'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          search: this.querySelector('input[name="country_filter"]'),
          closeButton: this.querySelector('.country-selector__close-button'),
          resetButton: this.querySelector('.country-filter__reset-button'),
          searchIcon: this.querySelector('.country-filter__search-icon'),
          liveRegion: this.querySelector('[data-country-search-results]'),
        };
        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
        this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
        this.addEventListener('focusout', this.closeSelector.bind(this));
        this.elements.button.addEventListener('click', this.openSelector.bind(this));
        this.pointerInside = false;
        this.isCountrySelector = this.elements.panel.classList.contains('country-selector');
        if (this.isCountrySelector && typeof this.elements.panel.showPopover === 'function') {
          this.elements.panel.setAttribute('popover', 'manual');
        }
        this.positionPanel = () => {
          if (!this.isCountrySelector || this.elements.panel.hidden) return;
          const viewport = window.visualViewport;
          const height = viewport?.height || window.innerHeight;
          const width = viewport?.width || window.innerWidth;
          const margin = Math.max(12, Math.min(56, height * .06));
          const style = this.elements.panel.style;
          style.setProperty('--localization-top', ((viewport?.offsetTop || 0) + margin) + 'px');
          style.setProperty('--localization-left', ((viewport?.offsetLeft || 0) + width / 2) + 'px');
          style.setProperty('--localization-width', Math.min(420, Math.max(200, width - 24)) + 'px');
          style.setProperty('--localization-height', Math.min(560, Math.max(80, height - margin * 2)) + 'px');
        };
        this.onPointerDown = (event) => {
          this.pointerInside = this.contains(event.target);
          if (!this.pointerInside && this.elements.button.getAttribute('aria-expanded') === 'true') this.hidePanel();
        };
        this.onPointerEnd = () => { clearTimeout(this.pointerTimer); this.pointerTimer = setTimeout(() => { this.pointerInside = false; }, 0); };
        this.querySelector('.country-selector__overlay')?.addEventListener('click', () => this.hidePanel());

        if (this.elements.search) {
          this.elements.search.addEventListener('keyup', this.filterCountries.bind(this));
          this.elements.search.addEventListener('focus', this.onSearchFocus.bind(this));
          this.elements.search.addEventListener('blur', this.onSearchBlur.bind(this));
          this.elements.search.addEventListener('keydown', this.onSearchKeyDown.bind(this));
        }
        if (this.elements.closeButton) {
          this.elements.closeButton.addEventListener('click', this.hidePanel.bind(this));
        }
        if (this.elements.resetButton) {
          this.elements.resetButton.addEventListener('click', this.resetFilter.bind(this));
          this.elements.resetButton.addEventListener('mousedown', (event) => event.preventDefault());
        }

        this.querySelectorAll('a').forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)));
      }

      connectedCallback() {
        document.addEventListener('pointerdown', this.onPointerDown, true);
        document.addEventListener('pointerup', this.onPointerEnd, true);
        document.addEventListener('pointercancel', this.onPointerEnd, true);
      }

      disconnectedCallback() {
        document.removeEventListener('pointerdown', this.onPointerDown, true);
        document.removeEventListener('pointerup', this.onPointerEnd, true);
        document.removeEventListener('pointercancel', this.onPointerEnd, true);
        clearTimeout(this.pointerTimer);
        clearTimeout(this.focusTimer);
        this.stopPositioning();
        if (this.elements.panel.hasAttribute('popover')) this.elements.panel.hidePopover();
      }

      stopPositioning() {
        window.removeEventListener('resize', this.positionPanel);
        window.visualViewport?.removeEventListener('resize', this.positionPanel);
        window.visualViewport?.removeEventListener('scroll', this.positionPanel);
      }

      hidePanel() {
        if (this.elements.panel.hasAttribute('popover')) this.elements.panel.hidePopover();
        this.stopPositioning();
        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.panel.setAttribute('hidden', true);
        if (this.elements.search) {
          this.elements.search.value = '';
          this.filterCountries();
          this.elements.search.setAttribute('aria-activedescendant', '');
        }
        document.body.classList.remove('overflow-hidden-mobile');
        document.querySelector('.menu-drawer')?.classList.remove('country-selector-open');
        if (this.header) this.header.preventHide = false;
      }

      onContainerKeyDown(event) {
        const focusableItems = Array.from(this.querySelectorAll('a')).filter(
          (item) => !item.parentElement.classList.contains('hidden')
        );
        let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        let itemToFocus;

        switch ((event.code || event.key || '').toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
            itemToFocus?.focus();
            break;
          case 'ARROWDOWN':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
            itemToFocus?.focus();
            break;
        }

        if (!this.elements.search) return;

        setTimeout(() => {
          focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
          if (focusedItemIndex > -1) {
            this.elements.search.setAttribute('aria-activedescendant', focusableItems[focusedItemIndex].id);
          } else {
            this.elements.search.setAttribute('aria-activedescendant', '');
          }
        });
      }

      onContainerKeyUp(event) {
        event.preventDefault();

        switch ((event.code || event.key || '').toUpperCase()) {
          case 'ESCAPE':
            if (this.elements.button.getAttribute('aria-expanded') == 'false') return;
            this.hidePanel();
            event.stopPropagation();
            this.elements.button.focus();
            break;
          case 'SPACE':
            if (this.elements.button.getAttribute('aria-expanded') == 'true') return;
            this.openSelector();
            break;
        }
      }

      onItemClick(event) {
        event.preventDefault();
        const form = this.querySelector('form');
        const value = event.currentTarget.dataset.value;
        if (!form || !value || !this.elements.input) return;
        this.elements.input.value = value;
        this.elements.button.setAttribute('aria-busy', 'true');
        // Let Shopify switch country, presentment currency and market together.
        HTMLFormElement.prototype.submit.call(form);
      }

      openSelector() {
        if (this.elements.button.getAttribute('aria-expanded') === 'true') { this.hidePanel(); return; }
        document.querySelectorAll('localization-form').forEach((form) => {
          if (form !== this && form.elements?.button.getAttribute('aria-expanded') === 'true') form.hidePanel();
        });
        this.elements.button.focus({preventScroll: true});
        this.elements.panel.removeAttribute('hidden');
        this.positionPanel();
        if (this.elements.panel.hasAttribute('popover')) this.elements.panel.showPopover();
        window.addEventListener('resize', this.positionPanel, {passive: true});
        window.visualViewport?.addEventListener('resize', this.positionPanel, {passive: true});
        window.visualViewport?.addEventListener('scroll', this.positionPanel, {passive: true});
        this.elements.button.setAttribute('aria-expanded', 'true');
        if (!document.body.classList.contains('overflow-hidden-tablet')) document.body.classList.add('overflow-hidden-mobile');
        if (this.elements.search && this.mql.matches) this.elements.search.focus({preventScroll: true});
        if (this.hasAttribute('data-prevent-hide') && this.header) this.header.preventHide = true;
        if (this.closest('.menu-drawer')) this.closest('.menu-drawer').classList.add('country-selector-open');
      }

      closeSelector(event) {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        // iOS can blur the button with relatedTarget=null before dispatching a tap.
        // Keep the list available until that pointer's click has selected its country.
        if (this.pointerInside) return;
        clearTimeout(this.focusTimer);
        this.focusTimer = setTimeout(() => {
          if (!this.pointerInside && !this.contains(document.activeElement)) this.hidePanel();
        }, 0);
      }

      normalizeString(str) {
        return str
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase();
      }

      filterCountries() {
        const searchValue = this.normalizeString(this.elements.search.value);
        const popularCountries = this.querySelector('.popular-countries');
        const allCountries = this.querySelectorAll('a');
        let visibleCountries = 0;

        this.elements.resetButton.classList.toggle('hidden', !searchValue);

        if (popularCountries) {
          popularCountries.classList.toggle('hidden', searchValue);
        }

        allCountries.forEach((item) => {
          const countryName = this.normalizeString(item.querySelector('.country').textContent);
          if (countryName.indexOf(searchValue) > -1) {
            item.parentElement.classList.remove('hidden');
            visibleCountries++;
          } else {
            item.parentElement.classList.add('hidden');
          }
        });

        if (this.elements.liveRegion) {
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.countrySelectorSearchCount.replace(
            '[count]',
            visibleCountries
          );
        }

        this.querySelector('.country-selector').scrollTop = 0;
        this.querySelector('.country-selector__list').scrollTop = 0;
      }

      resetFilter(event) {
        event.stopPropagation();
        this.elements.search.value = '';
        this.filterCountries();
        this.elements.search.focus();
      }

      onSearchFocus() {
        this.elements.searchIcon.classList.add('country-filter__search-icon--hidden');
      }

      onSearchBlur() {
        if (!this.elements.search.value) {
          this.elements.searchIcon.classList.remove('country-filter__search-icon--hidden');
        }
      }

      onSearchKeyDown(event) {
        if ((event.code || event.key || '').toUpperCase() === 'ENTER') {
          event.preventDefault();
        }
      }
    }
  );
}
